import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { portfolioStore } from '../../stores/portfolio-store';
import { Button } from '@privemind/components';
import { Asset } from '../../types';
import './delete-asset-modal.scss';
import { isError } from '../../lib/utils/type-guards';

interface DeleteAssetModalProps {
  asset: Asset;
  assetId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteAssetModal: React.FC<DeleteAssetModalProps> = observer(({
  asset,
  assetId,
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleDelete = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await portfolioStore.removeAsset(assetId);

      if (isError(response)) {
        setError(response.message || 'Failed to delete asset. Please try again.');
      } else {
        onClose();
      }
    } catch (error) {
      setError('An error occurred while deleting the asset.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="delete-asset-modal">
      <div className="delete-asset-modal__backdrop" onClick={onClose} />
      <div className="delete-asset-modal__content">
        <div className="delete-asset-modal__header">
          <h2>Delete Asset</h2>
          <button className="delete-asset-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="delete-asset-modal__body">
          <div className="delete-asset-modal__warning-icon">⚠️</div>
          <p className="delete-asset-modal__message">
            Are you sure you want to delete <strong>{asset.name} ({asset.symbol.toUpperCase()})</strong> from the <strong>{asset.bucket}</strong> bucket?
          </p>
          <p className="delete-asset-modal__submessage">
            This will permanently remove {asset.amount} {asset.symbol.toUpperCase()} from your portfolio. This action cannot be undone.
          </p>

          {error && (
            <div className="delete-asset-modal__error">
              {error}
            </div>
          )}
        </div>

        <div className="delete-asset-modal__actions">
          <Button
            color="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete Asset'}
          </Button>
        </div>
      </div>
    </div>
  );
});
